import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { type z } from 'zod';

import { queryKeysUnit } from '../queryKeys';

import { fetchUnitMappings } from './fetchUnitMappings';
import {
  type UnitMappingsResponse,
  type UnitMappingsSearchParams,
} from './types';
import { zodUnitMappingsSearchParamsSchema } from './validations';

/**
 * Get the query options for fetching unit mappings.
 * @param options - Optional configuration for the query.
 * @param queryParameters - The search parameters for fetching the unit mappings.
 * @returns The query options.
 */
export const getUnitMappingsQueryOptions = ({
  queryParameters = {},
  options = {},
}: {
  options?: Omit<UseQueryOptions<UnitMappingsResponse>, 'queryKey' | 'queryFn'>;
  queryParameters?: UnitMappingsSearchParams;
}) => {
  const validatedSearchParams =
    zodUnitMappingsSearchParamsSchema.parse(queryParameters);

  return queryOptions({
    async queryFn({ queryKey }) {
      const unitMappingsSearchParams = queryKey[3] as z.output<
        typeof zodUnitMappingsSearchParamsSchema
      >; // TODO: query keys should be structured as one object as first array element; getting queryKey[3] is brittle

      return fetchUnitMappings(unitMappingsSearchParams);
    },
    queryKey: queryKeysUnit.getAllMappings(validatedSearchParams),
    staleTime: ms('1m'),
    ...options,
  });
};

/**
 * React Query based custom hook for getting all unit mappings.
 * @param queryParameters - Search parameters for filtering unit mappings.
 *   - issuerId: Optional UUID to filter by issuer.
 *   - limit: Number of items per page (default: 1000 (!)).
 *   - offset: Number of items to skip (default: 0).
 *   - uncefactUnit: Optional UNCEFACT unit to filter by.
 * @param options - Optional React Query configuration (except 'queryKey' and 'queryFn').
 * @returns Query result containing the paginated list of unit mappings and query state.
 * @see https://app.dev.vestigas.com/redoc#tag/Units/operation/get_all_unit_mappings_units_unit_mapping_get
 */
export const useQueryUnitMappings = (
  queryParameters?: UnitMappingsSearchParams,
  options?: Omit<UseQueryOptions<UnitMappingsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery(
    getUnitMappingsQueryOptions({
      queryParameters,
      options,
    }),
  );
};
